import React, { useState, useRef, useEffect } from 'react';
import './ChatBubble.css';

const FONT_HREF = 'https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600;700&display=swap';

const QUICK_PROMPTS = [
  'How do I set up my SIM?',
  'How much does it cost?',
  'How does GoHighLevel work?',
  'Does TextLink send MMS?'
];

const WELCOME = `Hi — I'm the TextLink assistant. I can help with:

• Pricing and plans
• SIM setup & the Android app
• GoHighLevel integration
• REST API and webhooks
• Deliverability and A2P questions

Ask me anything, or tap a suggestion below.`;

const linkify = (text) => {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  // Preserve line breaks from the welcome message.
  const withBreaks = escaped.replace(/\n/g, '<br />');
  return withBreaks.replace(
    /(https?:\/\/[^\s<]+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer" class="cb-link">$1</a>'
  );
};

const ChatBubble = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: WELCOME }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!document.querySelector(`link[href="${FONT_HREF}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = FONT_HREF;
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    const node = bottomRef.current;
    if (!node) return;
    const container = node.parentElement;
    if (container) container.scrollTop = container.scrollHeight;
  }, [messages, loading]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const send = async (text) => {
    const content = text.trim();
    if (!content || loading) return;

    const updated = [...messages, { role: 'user', content }];
    setMessages(updated);
    setInput('');
    setLoading(true);

    try {
      const response = await fetch(`${global.SERVIP}/chatbot/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: updated })
      });
      const data = await response.json();
      if (data.ok) {
        setMessages((prev) => [...prev, { role: 'assistant', content: data.reply }]);
      } else {
        setMessages((prev) => [...prev, {
          role: 'assistant',
          content: 'Sorry, something went wrong. Try again or email help@textlinksms.com.'
        }]);
      }
    } catch {
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: 'Could not reach the server. Please try again in a moment.'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  const showQuickPrompts = messages.length === 1 && !loading;

  return (
    <>
      {/* Chat window */}
      <div className={`cb-window ${open ? 'cb-window--open' : ''}`}>
        <div className="cb-header">
          <div className="cb-header-left">
            <div className="cb-avatar">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2 L2 7 L12 12 L22 7 L12 2 Z" />
                <path d="M2 17 L12 22 L22 17" />
                <path d="M2 12 L12 17 L22 12" />
              </svg>
            </div>
            <div>
              <div className="cb-header-title">
                TextLink assistant
                <span className="cb-live-dot" aria-hidden="true" />
              </div>
              <div className="cb-header-sub">Usually replies in seconds</div>
            </div>
          </div>
          <button className="cb-close" onClick={() => setOpen(false)} aria-label="Close chat">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="cb-messages">
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`cb-msg ${msg.role === 'user' ? 'cb-msg--user' : 'cb-msg--bot'}`}
              dangerouslySetInnerHTML={{ __html: linkify(msg.content) }}
            />
          ))}
          {loading && (
            <div className="cb-msg cb-msg--bot cb-typing">
              <span></span><span></span><span></span>
            </div>
          )}

          {showQuickPrompts && (
            <div className="cb-quick">
              {QUICK_PROMPTS.map((q) => (
                <button key={q} className="cb-quick-chip" onClick={() => send(q)}>
                  {q}
                </button>
              ))}
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        <div className="cb-input-bar">
          <input
            ref={inputRef}
            className="cb-input"
            placeholder="Ask about SIM setup, pricing, GHL…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
          />
          <button
            className="cb-send"
            onClick={() => send(input)}
            disabled={loading || !input.trim()}
            aria-label="Send message"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12 L19 12" />
              <path d="M12 5 L19 12 L12 19" />
            </svg>
          </button>
        </div>

        <div className="cb-foot">
          AI-powered · responses may be imperfect ·{' '}
          <a href="mailto:help@textlinksms.com">email support</a>
        </div>
      </div>

      {/* Floating bubble */}
      <button
        className={`cb-bubble ${open ? 'cb-bubble--hidden' : ''}`}
        onClick={() => setOpen(true)}
        aria-label="Open chat"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
        </svg>
        <span className="cb-bubble-ping" aria-hidden="true" />
      </button>
    </>
  );
};

export default ChatBubble;
